import { history } from '@lib/history';
import {
  getAuthTokens,
  login,
  register,
  resendVerificationEmail,
  resendVerificationPhone,
  verifyPhone,
} from '@services/external/auth.api';
import { updateUserRole } from '@services/external/user.api';
import { TokenService } from '@services/internal';
import { actions as authActions } from '@store/ducks/auth';
import jwtDecode from 'jwt-decode';
import { takeLeading } from 'redux-saga/effects';
import { call, delay, put, select, takeLatest } from 'typed-redux-saga/macro';
import TStore from 'types/store';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '../../constants/local-storage';
import { STEPS } from '../../pages/auth/registration/registration.types';
import { authActionTypes } from '../ducks/auth';
import { actions as userActions } from '../ducks/users';
import { IDecodedLoginToken, TMro } from './../../types/auth-token.types';

function* getLoginRequest({ payload }: ReturnType<typeof authActions.getLoginRequest>) {
  yield put({
    type: authActionTypes.SET_LOGIN_LOADING,
    payload: true,
  });

  try {
    const { data: loginData } = yield* call(() => login(payload.email, payload.password));
    const loginToken = loginData.loginToken;
    const decodedLoginToken = jwtDecode<IDecodedLoginToken>(loginToken);

    /**
     * User has not verified email, so show wizard from registration
     */
    if (decodedLoginToken.phoneRequire === 1 && decodedLoginToken.phoneVerify === 0) {
      yield put({
        type: authActionTypes.SET_REGISTER_TOKEN,
        payload: loginToken,
      });
      history.replace('/registration/code');
      return;
    }

    /**
     * Get real tokens from login token,
     * and store them with roles to LS
     */
    const { data: tokens } = yield* call(getAuthTokens, loginToken);
    const session = yield* call(TokenService.setSession, tokens);

    const mros = session?.decodedUserToken.mro?.split(',') as TMro[];

    /**
     * Check if user has mro to go to dashboard,
     * or show registration wizard
     */
    if (mros.includes('manager')) {
      yield put(
        userActions.getCurrentUserRequest({
          withXmpp: true,
        }),
      );
    } else {
      history.replace('/registration/role');
    }
  } catch (e) {
    //TODO: WEB figure out error codes
    console.log('getLoginRequest', e);
    const message = (e as any).data?.message_code || 'something_wrong';
    yield put({
      type: authActionTypes.GET_LOGIN_ERROR,
      payload: `server_error.${message}`,
    });
  } finally {
    yield* put({
      type: authActionTypes.SET_LOGIN_LOADING,
      payload: false,
    });
  }
}

function* getRegisterRequest({ payload }: ReturnType<typeof authActions.getRegisterRequest>) {
  yield put({
    type: authActionTypes.SET_REGISTER_LOADING,
    payload: true,
  });

  yield put({
    type: authActionTypes.SET_REGISTER_ERROR,
    payload: '',
  });

  yield put({
    type: authActionTypes.ADD_REGISTER_DATA,
    payload,
  });

  try {
    const regRequest = yield* call(() =>
      register({
        first_name: payload.firstName,
        last_name: payload.lastName,
        email: payload.email,
        password: payload.password,
        password_confirmation: payload.repeated,
        third_party_role: 'client',
        phone_number:
          payload.phoneNumber.length > 4 ? payload.phoneNumber.replace(/ /gm, '') : null,
        termsAgreement: true,
        privacyPolicy: true,
      }),
    );

    yield put({
      type: authActionTypes.SET_REGISTER_TOKEN,
      payload: regRequest.data.loginToken,
    });
    yield put({
      type: authActionTypes.SET_REGISTER_LOADING,
      payload: false,
    });

    yield put({
      type: authActionTypes.SET_CURRENT_REG_STEP,
      payload: STEPS.CODE,
    });
  } catch (err: any) {
    console.log(err);
    const { message_code } = err?.data || 'something_wrong';
    yield put({
      type: authActionTypes.SET_REGISTER_ERROR,
      payload: message_code,
    });
    yield put({
      type: authActionTypes.SET_REGISTER_LOADING,
      payload: false,
    });
  }
}

function* getCodeVerifyRequest({
  payload,
}: ReturnType<typeof authActions.getPhoneCodeVerifyRequest>) {
  yield put({
    type: authActionTypes.SET_REGISTER_LOADING,
    payload: true,
  });

  yield put({
    type: authActionTypes.SET_REGISTER_ERROR,
    payload: '',
  });

  const registerToken = yield* select((store: TStore) => store.auth.registerToken);

  try {
    const { data } = yield* call(verifyPhone, payload, registerToken);
    const loginToken = 'loginToken' in data ? data.loginToken : '';

    const {
      data: { accessToken, refreshToken },
    } = yield* call(getAuthTokens, loginToken);

    yield* call(TokenService.setSession, { accessToken, refreshToken });

    yield put({
      type: authActionTypes.SET_REGISTER_LOADING,
      payload: false,
    });

    yield put({
      type: authActionTypes.SET_CURRENT_REG_STEP,
      payload: STEPS.ROLE,
    });
  } catch (err: any) {
    const { message_code } = err?.data || 'something_wrong';
    console.log('getCodeVerifyRequest', err);

    yield put({
      type: authActionTypes.SET_REGISTER_ERROR,
      payload: message_code,
    });
    yield put({
      type: authActionTypes.SET_REGISTER_LOADING,
      payload: false,
    });
  }
}

function* getUpdateRoleRequest() {
  yield put({
    type: authActionTypes.SET_REGISTER_LOADING,
    payload: true,
  });

  yield put({
    type: authActionTypes.SET_REGISTER_ERROR,
    payload: '',
  });

  try {
    yield* call(() => updateUserRole(['client', 'manager']));
    yield* call(TokenService.refreshAccessToken);

    yield put({
      type: authActionTypes.SET_CURRENT_REG_STEP,
      payload: STEPS.SUCCESS,
    });
  } catch (err: any) {
    const { message } = err?.data || 'something_wrong';

    yield put({
      type: authActionTypes.SET_REGISTER_ERROR,
      payload: message,
    });
    yield put({
      type: authActionTypes.SET_REGISTER_LOADING,
      payload: false,
    });
  }
}

function* cancelAutoLogin() {
  yield* delay(300); // avoid flashing screen
  yield* put({
    type: authActionTypes.SET_LOADING,
    payload: false,
  });

  yield* call(() => localStorage.removeItem(ACCESS_TOKEN));
  yield* call(() => localStorage.removeItem(REFRESH_TOKEN));
}

function* getTryLoginRequest() {
  const session = yield* call(TokenService.getSession);

  if (!session) {
    yield call(cancelAutoLogin);
    return;
  }

  try {
    const decodedToken = session.decodedUserToken;
    const mros = decodedToken.mro?.split(',') as TMro[];

    if (mros.includes('manager')) {
      yield put(
        userActions.getCurrentUserRequest({
          withXmpp: true,
        }),
      );
    } else {
      yield* put({
        type: authActionTypes.SET_LOADING,
        payload: false,
      });
      history.replace('/registration/role');
    }
  } catch (error) {
    console.log('getTryLoginRequest', error);
    yield call(cancelAutoLogin);
  }
}

function* getResendEmailRequest() {
  const registerToken = yield* select((store: TStore) => store.auth.registerToken);

  try {
    yield* call(resendVerificationEmail, registerToken);
    yield put({
      type: authActionTypes.SET_HAS_RESENT_EMAIL,
      payload: true,
    });
  } catch (e: any) {
    const { message_code } = e?.data;
    yield put({
      type: authActionTypes.GET_RESENT_EMAIL_ERROR,
      payload: message_code,
    });
  }
}

function* getResendPhoneRequest() {
  const registerToken = yield* select((store: TStore) => store.auth.registerToken);

  try {
    yield* call(resendVerificationPhone, registerToken);
    yield put({
      type: authActionTypes.SET_HAS_RESENT_PHONE,
      payload: true,
    });
  } catch (e: any) {
    const { message_code } = e?.data;
    yield put({
      type: authActionTypes.GET_RESENT_PHONE_ERROR,
      payload: message_code,
    });
  }
}

function* authSaga() {
  yield takeLatest(authActionTypes.GET_LOGIN_REQUEST, getLoginRequest);
  yield takeLatest(authActionTypes.GET_REGISTER_REQUEST, getRegisterRequest);
  yield takeLatest(authActionTypes.GET_CODE_VERIFY_REQUEST, getCodeVerifyRequest);
  yield takeLatest(authActionTypes.SET_ROLE, getUpdateRoleRequest);
  yield takeLatest(authActionTypes.GET_RESEND_EMAIL_REQUEST, getResendEmailRequest);
  yield takeLatest(authActionTypes.GET_RESEND_PHONE_REQUEST, getResendPhoneRequest);

  yield takeLeading(authActionTypes.GET_TRY_LOGIN_REQ, getTryLoginRequest);
}

export default authSaga;
